import React, {useMemo} from 'react'
import styled from '@emotion/styled'

import {Box} from '@vio/atlas/helpers/Box'
import {Divider} from '@vio/atlas/src/components/designSystem/Divider'
import {Icon} from '@vio/atlas/src/components/designSystem/Icon'
import {Tag, TagVariant} from '@vio/atlas/src/components/designSystem/Tag'
import {useDeviceLayout} from '@vio/atlas/src/context/deviceLayout'
import {cssTheme} from '@vio/atlas/src/themes'
import {Text} from '@vio/atlas/Text'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {Room} from '@vio/core/src/room/types/room'

import {AmenitiesListExpander} from './AmenitiesListExpander'
import {rearrangeAmenitiesByRankedCategory} from './utils'

type Props = {
  amenities: Room['amenitiesList']
}

const ColumnBox = styled.div`
  columns: 2;
  column-width: 50%;
  column-gap: ${cssTheme.layout.spacing.s250};
`

const AmenitiesBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-left: ${cssTheme.layout.spacing.s200};
  padding-bottom: ${cssTheme.layout.spacing.s200};
  flex-wrap: wrap;
  row-gap: 0;
  column-gap: ${cssTheme.layout.spacing.s250};
  break-inside: avoid;
`

const ListLayout = ({children}: {children: React.ReactNode}) => {
  const {isMobile} = useDeviceLayout()

  if (isMobile) {
    return (
      <Box display="flex" paddingLeft="s600" flexDirection="column" gap="s200">
        {children}
      </Box>
    )
  }
  return (
    <Box paddingLeft="s600">
      <ColumnBox>{children}</ColumnBox>
    </Box>
  )
}

const AmenitiesGroupDivider = ({isLast}: {isLast: boolean}) => {
  const {isMobile} = useDeviceLayout()

  if (!isMobile || isLast) return null

  return (
    <Box paddingTop="s500">
      <Divider />
    </Box>
  )
}

const getTagVariant = (tagId: string): TagVariant =>
  tagId === '1' ? 'success' : 'neutral'

export const RoomAmenities = ({amenities}: Props) => {
  const amenitiesGroups = useMemo(
    () => rearrangeAmenitiesByRankedCategory(amenities),
    [amenities]
  )

  return (
    <>
      <Text variant="titleL">
        <FormattedMessageWrapper id="amenities" defaultMessage="Amenities" />
      </Text>
      <AmenitiesListExpander amenitiesAmount={amenities?.length ?? 0}>
        {amenitiesGroups.map((group, index) => (
          <React.Fragment key={group.categoryId}>
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="flex-start"
              gap="s300"
              paddingBottom="s250"
              paddingTop="s500"
            >
              <Icon name={group.categoryIcon} size="md" />
              <Text variant="titleM">{group.category}</Text>
            </Box>
            <ListLayout>
              {group.amenities?.map(amenity => {
                return (
                  <AmenitiesBox key={amenity.id}>
                    <Text key={amenity.id}>{amenity.title}</Text>
                    {amenity.tag && (
                      <Tag
                        variant={getTagVariant(amenity.tag.id)}
                        size="sm"
                        key={amenity.tag.id}
                      >
                        <Text variant="labelXS">{amenity.tag.name}</Text>
                      </Tag>
                    )}
                  </AmenitiesBox>
                )
              })}
            </ListLayout>
            <AmenitiesGroupDivider
              isLast={index === amenitiesGroups.length - 1}
            />
          </React.Fragment>
        ))}
      </AmenitiesListExpander>
    </>
  )
}
